import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Select, MenuItem, FormControl,
  InputLabel, Box, FormHelperText, IconButton, Typography,
  Checkbox, Divider, Autocomplete,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { TimeSelect } from '../UI/TimeSelect';
import {
  TaskData, TaskCategory, TaskPriority, SubTaskItem,
  CATEGORY_LABELS, PRIORITY_LABELS,
  CreateTaskRequest, UpdateTaskRequest,
} from '../../types/task';

type Mode = 'create' | 'edit';

interface Props {
  open:                boolean;
  mode:                Mode;
  task?:               TaskData | null;
  isLoading:           boolean;
  companyNameOptions?: string[];
  onSave:              (data: CreateTaskRequest | UpdateTaskRequest) => Promise<void>;
  onClose:             () => void;
}

interface FormState {
  title:                string;
  description:          string;
  category:             TaskCategory | '';
  priority:             TaskPriority | '';
  due_date:             string;
  scheduled_time_start: string;
  location:             string;
  company_name:         string;
  sub_tasks:            SubTaskItem[];
}

const INITIAL: FormState = {
  title:                '',
  description:          '',
  category:             '',
  priority:             TaskPriority.MEDIUM,
  due_date:             '',
  scheduled_time_start: '',
  location:             '',
  company_name:         '',
  sub_tasks:            [],
};

function generateId(): string {
  return Math.random().toString(36).slice(2, 10);
}

export const TaskForm: React.FC<Props> = ({ open, mode, task, isLoading, companyNameOptions = [], onSave, onClose }) => {
  const [form, setForm]                     = useState<FormState>(INITIAL);
  const [initialForm, setInitialForm]       = useState<FormState>(INITIAL);
  const [errors, setErrors]                 = useState<Partial<Record<keyof FormState, string>>>({});
  const [confirmClose, setConfirmClose]     = useState(false);

  useEffect(() => {
    if (open) {
      if (mode === 'edit' && task) {
        const editForm: FormState = {
          title:                task.title,
          description:          task.description ?? '',
          category:             task.category,
          priority:             task.priority,
          due_date:             task.due_date ?? '',
          scheduled_time_start: task.scheduled_time_start ?? '',
          location:             task.location ?? '',
          company_name:         task.company_name ?? '',
          sub_tasks:            task.sub_tasks ?? [],
        };
        setForm(editForm);
        setInitialForm(editForm);
      } else {
        setForm(INITIAL);
        setInitialForm(INITIAL);
      }
      setErrors({});
      setConfirmClose(false);
    }
  }, [open, mode, task]);

  const set = (key: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleTimeChange = (value: string) => {
    setForm((prev) => ({ ...prev, scheduled_time_start: value }));
  };

  const addSubTask = () => {
    setForm((prev) => ({
      ...prev,
      sub_tasks: [...prev.sub_tasks, { id: generateId(), title: '', is_completed: false }],
    }));
  };

  const updateSubTaskTitle = (index: number, title: string) => {
    setForm((prev) => {
      const updated = [...prev.sub_tasks];
      updated[index] = { ...updated[index], title };
      return { ...prev, sub_tasks: updated };
    });
  };

  const removeSubTask = (index: number) => {
    setForm((prev) => ({
      ...prev,
      sub_tasks: prev.sub_tasks.filter((_, i) => i !== index),
    }));
  };

  const validate = (): boolean => {
    const errs: typeof errors = {};
    if (!form.title.trim()) errs.title = 'タスク名は必須です';
    else if (form.title.length > 255) errs.title = '255文字以内で入力してください';
    if (!form.category) errs.category = 'カテゴリを選択してください';
    if (!form.priority) errs.priority = '優先度を選択してください';
    if (form.description.length > 300) errs.description = '300文字以内で入力してください';
    if (!form.due_date) errs.due_date = '締切日は必須です';
    else if (!/^\d{4}-\d{2}-\d{2}$/.test(form.due_date)) errs.due_date = '正しい日付を入力してください（YYYY-MM-DD）';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    const validSubTasks = form.sub_tasks.filter((s) => s.title.trim() !== '');
    const payload: CreateTaskRequest = {
      title:                form.title.trim(),
      description:          form.description.trim() || null,
      category:             form.category as TaskCategory,
      priority:             form.priority as TaskPriority,
      due_date:             form.due_date,
      scheduled_time_start: form.scheduled_time_start || null,
      location:             form.location.trim() || null,
      sub_tasks:            validSubTasks.length > 0 ? validSubTasks : null,
      company_name:         form.company_name.trim() || null,
    };
    await onSave(payload);
  };

  // 編集モード：開いた時点の値から変更されたか比較
  // 新規作成モード：何か1つでも入力されたか確認（時刻・優先度のデフォルト値は除外）
  const isDirty = mode === 'edit'
    ? form.title              !== initialForm.title              ||
      form.description        !== initialForm.description        ||
      form.category           !== initialForm.category           ||
      form.priority           !== initialForm.priority           ||
      form.due_date           !== initialForm.due_date           ||
      form.scheduled_time_start !== initialForm.scheduled_time_start ||
      form.location           !== initialForm.location           ||
      form.company_name       !== initialForm.company_name       ||
      JSON.stringify(form.sub_tasks) !== JSON.stringify(initialForm.sub_tasks)
    : form.title !== '' ||
      form.description !== '' ||
      form.company_name !== '' ||
      form.location !== '' ||
      form.sub_tasks.length > 0 ||
      form.due_date !== '';

  const handleCloseRequest = () => {
    if (isDirty) {
      setConfirmClose(true);
    } else {
      onClose();
    }
  };

  const handleConfirmDiscard = () => {
    setConfirmClose(false);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleCloseRequest} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {mode === 'create' ? 'タスクを作成' : 'タスクを編集'}
            <IconButton onClick={handleCloseRequest} size="small"><CloseIcon /></IconButton>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <TextField
                label="タスク名 *"
                value={form.title}
                onChange={set('title')}
                error={!!errors.title}
                helperText={errors.title}
                fullWidth
                disabled={isLoading}
              />
              <TextField
                label="説明（メモ）"
                value={form.description}
                onChange={set('description')}
                error={!!errors.description}
                helperText={errors.description}
                multiline
                rows={3}
                fullWidth
                disabled={isLoading}
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl fullWidth error={!!errors.category}>
                  <InputLabel>カテゴリ *</InputLabel>
                  <Select value={form.category} label="カテゴリ *" onChange={set('category')} disabled={isLoading}>
                    {Object.values(TaskCategory).map((c) => (
                      <MenuItem key={c} value={c}>{CATEGORY_LABELS[c]}</MenuItem>
                    ))}
                  </Select>
                  {errors.category && <FormHelperText>{errors.category}</FormHelperText>}
                </FormControl>
                <FormControl fullWidth error={!!errors.priority}>
                  <InputLabel>優先度 *</InputLabel>
                  <Select value={form.priority} label="優先度 *" onChange={set('priority')} disabled={isLoading}>
                    {Object.values(TaskPriority).map((p) => (
                      <MenuItem key={p} value={p}>{PRIORITY_LABELS[p]}</MenuItem>
                    ))}
                  </Select>
                  {errors.priority && <FormHelperText>{errors.priority}</FormHelperText>}
                </FormControl>
              </Box>
              <TextField
                label="締切日 *"
                type="date"
                value={form.due_date}
                onChange={set('due_date')}
                error={!!errors.due_date}
                helperText={errors.due_date ?? '例: 2026-04-30'}
                InputLabelProps={{ shrink: true }}
                fullWidth
                disabled={isLoading}
              />

              <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                <Box sx={{ flex: 1 }}>
                  <TimeSelect
                    label="開始時刻（任意）"
                    value={form.scheduled_time_start}
                    onChange={handleTimeChange}
                    disabled={isLoading}
                  />
                </Box>
                {form.scheduled_time_start && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="inherit"
                    onClick={() => setForm((prev) => ({ ...prev, scheduled_time_start: '' }))}
                    disabled={isLoading}
                    sx={{ whiteSpace: 'nowrap', mb: '2px' }}
                  >
                    クリア
                  </Button>
                )}
              </Box>

              <Autocomplete
                freeSolo
                options={companyNameOptions}
                value={form.company_name}
                onChange={(_e, newValue) => {
                  const next = typeof newValue === 'string' ? newValue : '';
                  setForm((prev) => ({ ...prev, company_name: next }));
                  setErrors((prev) => ({ ...prev, company_name: undefined }));
                }}
                onInputChange={(_e, value, reason) => {
                  // reset は親からの value 同期などで呼ばれ、未編集なのに dirty になるので無視する
                  if (reason === 'input' || reason === 'clear') {
                    setForm((prev) => ({ ...prev, company_name: value }));
                    setErrors((prev) => ({ ...prev, company_name: undefined }));
                  }
                }}
                disabled={isLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="会社名（任意）"
                    placeholder="例: テックイノベーション株式会社"
                    fullWidth
                  />
                )}
              />
              <TextField
                label="場所（任意）"
                value={form.location}
                onChange={set('location')}
                placeholder="例: 東京本社、オンライン"
                fullWidth
                disabled={isLoading}
              />

              <Divider />

              {/* サブタスク */}
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" fontWeight={600} color="text.secondary">
                    チェックリスト（任意）
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={addSubTask}
                    disabled={isLoading}
                  >
                    追加
                  </Button>
                </Box>
                {form.sub_tasks.map((item, index) => (
                  <Box key={item.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                    <Checkbox size="small" checked={item.is_completed} disabled sx={{ p: 0.5 }} />
                    <TextField
                      size="small"
                      placeholder={`項目 ${index + 1}`}
                      value={item.title}
                      onChange={(e) => updateSubTaskTitle(index, e.target.value)}
                      fullWidth
                      disabled={isLoading}
                    />
                    <IconButton size="small" onClick={() => removeSubTask(index)} disabled={isLoading}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                {form.sub_tasks.length === 0 && (
                  <Typography variant="caption" color="text.disabled">
                    「追加」ボタンでチェックリスト項目を追加できます
                  </Typography>
                )}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseRequest} disabled={isLoading}>キャンセル</Button>
            <Button onClick={handleSave} variant="contained" disabled={isLoading}>
              {isLoading ? '保存中...' : '保存'}
            </Button>
          </DialogActions>
        </Dialog>

      {/* 離脱確認ダイアログ */}
      <Dialog open={confirmClose} onClose={() => setConfirmClose(false)}>
        <DialogTitle>入力内容を破棄しますか？</DialogTitle>
        <DialogContent>
          <Typography>入力中の内容は保存されません。閉じてもよいですか？</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmClose(false)}>入力に戻る</Button>
          <Button onClick={handleConfirmDiscard} color="error">
            破棄して閉じる
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
